import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useEffect, useMemo, useState } from 'react'
import {
  Container,
  Paper,
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  IconButton,
  Alert,
} from '@mui/material'
import { Save, Delete, Add, ArrowBack, Refresh } from '@mui/icons-material'
import { accountsService } from '../services/accounts-service'
import { snapshotsService } from '../services/snapshots-service'
import { AccountWithBalance, Snapshot } from '../types/api'
import { InstitutionMark } from '../components/InstitutionMark'
import { formatCurrency as fmtCurrency } from '../utils/currency'
import { useBaseCurrency } from '../stores/currency-store'
import { PropertyPanel } from '../components/PropertyPanel'

export const Route = createFileRoute('/accounts/$id')({
  component: AccountSnapshotsPage,
})

function AccountSnapshotsPage() {
  const baseCurrency = useBaseCurrency()
  const { id } = Route.useParams()
  const navigate = useNavigate()

  const [account, setAccount] = useState<AccountWithBalance | null>(null)
  const [snapshots, setSnapshots] = useState<Snapshot[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [newDate, setNewDate] = useState<string>('')
  const [newAmount, setNewAmount] = useState<string>('')
  const [newDescription, setNewDescription] = useState<string>('')

  useEffect(() => {
    const load = async () => {
      setIsLoading(true)
      setError(null)
      try {
        // Read from with-balances, not getAccount.
        //
        // Two reasons. httpClient.get returns the parsed BODY, so `.data` was
        // always undefined here and the header rendered a phantom account
        // called "Account" worth $0.00 with a blank currency -- for an account
        // holding real money. And the plain account endpoint carries no
        // balance at all; with-balances derives it from the ledger, which is
        // the figure every other screen shows.
        const withBalances = await accountsService.getAccountsWithBalances()
        const balanced = ((withBalances as any)?.data ?? withBalances ?? []) as any[]
        const found = Array.isArray(balanced) ? balanced.find((a) => a.id === id) : undefined
        if (found) {
          setAccount(found as any)
        } else {
          const accResp = await accountsService.getAccount(id)
          setAccount((((accResp as any)?.data) ?? accResp) as any)
        }
        const listResp = await snapshotsService.getSnapshots({ account_id: id, per_page: 1000 })
        // Defensive: the response is paginated, but an account with no
        // snapshots can come back without the inner `data` array at all, and
        // reading `.data.data` on it threw "Cannot read properties of
        // undefined" -- which surfaced as a blank crashed page rather than
        // "no history yet". That is now the common case, because the Plaid,
        // FX and CSV importers post journal entries and never write snapshots.
        const items = (listResp as any)?.data?.data ?? (listResp as any)?.data ?? []
        const list = Array.isArray(items) ? items : []
        list.sort((a: Snapshot, b: Snapshot) => a.date.localeCompare(b.date))
        setSnapshots(list)
      } catch (e: any) {
        setError(e?.message || 'Failed to load account snapshots')
      } finally {
        setIsLoading(false)
      }
    }
    load()
  }, [id])

  const saveAmount = async (s: Snapshot, nextAmount: string) => {
    try {
      await snapshotsService.updateSnapshot(s.id, { balance: nextAmount })
      setSnapshots(prev => prev.map(p => p.id === s.id ? { ...p, balance: nextAmount } : p))
    } catch (e: any) {
      setError(e?.message || 'Failed to update snapshot')
    }
  }

  const deleteSnapshot = async (s: Snapshot) => {
    try {
      await snapshotsService.deleteSnapshot(s.id)
      setSnapshots(prev => prev.filter(p => p.id !== s.id))
    } catch (e: any) {
      setError(e?.message || 'Failed to delete snapshot')
    }
  }

  const addSnapshot = async () => {
    if (!newDate || !newAmount) {
      setError('Please enter date and amount')
      return
    }
    try {
      const resp = await snapshotsService.createSnapshot({
        account_id: id,
        date: newDate,
        balance: newAmount,
        description: newDescription,
        source: 'Manual',
      })
      const created = resp.data
      setSnapshots(prev => {
        const next = [...prev, created]
        next.sort((a, b) => a.date.localeCompare(b.date))
        return next
      })
      setNewDate('')
      setNewAmount('')
      setNewDescription('')
      setError(null)
    } catch (e: any) {
      setError(e?.message || 'Failed to add snapshot')
    }
  }

  // This page shows ONE account, so every figure on it is denominated in that
  // account's currency. Formatting it as CAD would restate a USD balance as a
  // Canadian one.
  const formatCurrency = (amount: string) =>
    fmtCurrency(amount, account?.currency || baseCurrency, { base: baseCurrency })

  return (
    <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate({ to: '/dashboard' })}>
            Back
          </Button>
          <Typography variant="h5">Account Snapshots</Typography>
        </Box>
        <Button startIcon={<Refresh />} onClick={() => window.location.reload()}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <InstitutionMark
              name={account?.institution || account?.name}
              size={40}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {account?.name || 'Account'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {account?.institution ? `${account.institution} • ` : ''}
                Current: {formatCurrency(account?.current_amount || '0')} • Currency: {account?.currency || '—'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Property, financing and payments. Renders nothing for an
          ordinary bank account, which has no equity story to tell. */}
      <PropertyPanel accountId={id} />

      <Paper>
        <TableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={4}>Loading snapshots...</TableCell>
                </TableRow>
              ) : snapshots.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>No snapshots found</TableCell>
                </TableRow>
              ) : (
                snapshots.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell>{new Date(s.date).toLocaleDateString()}</TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        <TextField
                          size="small"
                          value={s.balance}
                          onChange={(e)=>{
                            const v = e.target.value
                            setSnapshots(prev => prev.map(p => p.id===s.id ? { ...p, balance: v } : p))
                          }}
                          sx={{ width: 140 }}
                        />
                        <IconButton color="primary" onClick={()=>saveAmount(s, s.balance)}>
                          <Save />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={s.description || ''}
                        onChange={(e)=>{
                          const v = e.target.value
                          setSnapshots(prev => prev.map(p => p.id===s.id ? { ...p, description: v } : p))
                        }}
                        onBlur={async ()=>{
                          try { await snapshotsService.updateSnapshot(s.id, { description: s.description || '' }) } catch {}
                        }}
                        fullWidth
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton color="error" onClick={()=>deleteSnapshot(s)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
              <TableRow>
                <TableCell>
                  <TextField size="small" placeholder="YYYY-MM-DD" value={newDate} onChange={(e)=>setNewDate(e.target.value)} />
                </TableCell>
                <TableCell align="right">
                  <TextField size="small" placeholder="Amount" value={newAmount} onChange={(e)=>setNewAmount(e.target.value)} sx={{ width: 140 }} />
                </TableCell>
                <TableCell>
                  <TextField size="small" placeholder="Description" value={newDescription} onChange={(e)=>setNewDescription(e.target.value)} fullWidth />
                </TableCell>
                <TableCell align="center">
                  <Button startIcon={<Add />} onClick={addSnapshot} variant="contained" size="small">Add</Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  )
}